'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/utils/cn';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { AboutTimelineSection } from '@/types/aboutus';

export type HistoryTimelineTheme = 'light' | 'dark';

type HistoryItem = {
  id: string;
  year: string;
  title: string;
  description: string;
  image: string;
};

const DEFAULT_HEADING = 'A Decade of Proven Trust';
const DEFAULT_SUBHEADING =
  "From our first pour in 2012 to defining the national skyline of 2025, witness Wonder Cement's rapid ascent.";

function getSiteHeaderHeight() {
  if (typeof window === 'undefined') return 88;

  const header = document.getElementById('site-header');
  if (header) {
    return header.getBoundingClientRect().height;
  }

  const cssValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--site-header-height')
    .trim();
  const headerHeight = Number.parseFloat(cssValue);

  return Number.isFinite(headerHeight) ? headerHeight : 88;
}

const DESKTOP_ACTIVE_VISIBILITY_RATIO = 0.5;

function getItemVisibleHeight(rect: DOMRect, viewportHeight = window.innerHeight) {
  return Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
}

function itemQualifiesForDesktopActive(rect: DOMRect, viewportHeight = window.innerHeight) {
  const visibleHeight = getItemVisibleHeight(rect, viewportHeight);
  if (visibleHeight <= 0) return false;

  const sectionVisibility = rect.height > 0 ? visibleHeight / rect.height : 0;
  const viewportCoverage = visibleHeight / viewportHeight;

  return (
    sectionVisibility >= DESKTOP_ACTIVE_VISIBILITY_RATIO ||
    viewportCoverage >= DESKTOP_ACTIVE_VISIBILITY_RATIO
  );
}

function getDesktopActiveIndexFromViewport(items: Array<HTMLElement | null>) {
  const viewportHeight = window.innerHeight;
  const viewportCenter = viewportHeight / 2;

  let fallbackIdx = 0;
  let fallbackCoverage = -1;
  let activeIdx = -1;
  let bestCenterDistance = Number.POSITIVE_INFINITY;

  items.forEach((item, idx) => {
    if (!item) return;

    const rect = item.getBoundingClientRect();
    const visibleHeight = getItemVisibleHeight(rect, viewportHeight);
    if (visibleHeight <= 0) return;

    const viewportCoverage = visibleHeight / viewportHeight;

    if (viewportCoverage > fallbackCoverage) {
      fallbackCoverage = viewportCoverage;
      fallbackIdx = idx;
    }

    if (itemQualifiesForDesktopActive(rect, viewportHeight)) {
      const itemCenter = rect.top + rect.height / 2;
      const centerDistance = Math.abs(itemCenter - viewportCenter);

      if (centerDistance < bestCenterDistance) {
        bestCenterDistance = centerDistance;
        activeIdx = idx;
      }
    }
  });

  return activeIdx >= 0 ? activeIdx : fallbackIdx;
}

const MOBILE_ACTIVE_VISIBILITY_RATIO = 0.5;

function getMobileVisibleViewportBounds(mobileBarHeight: number) {
  const headerHeight = getSiteHeaderHeight();
  const top = headerHeight + mobileBarHeight;
  const bottom = window.innerHeight;

  return {
    top,
    bottom,
    height: Math.max(bottom - top, 1),
    center: top + Math.max(bottom - top, 1) / 2,
  };
}

function getItemVisibleHeightInBounds(rect: DOMRect, top: number, bottom: number) {
  return Math.max(0, Math.min(rect.bottom, bottom) - Math.max(rect.top, top));
}

function getMobileActiveIndexFromViewport(
  items: Array<HTMLElement | null>,
  mobileBarHeight: number,
) {
  const {
    top,
    bottom,
    height: visibleViewportHeight,
    center: viewportCenter,
  } = getMobileVisibleViewportBounds(mobileBarHeight);

  let activeIdx = -1;
  let bestCenterDistance = Number.POSITIVE_INFINITY;

  items.forEach((item, idx) => {
    if (!item) return;

    const rect = item.getBoundingClientRect();
    const visibleHeight = getItemVisibleHeightInBounds(rect, top, bottom);
    if (visibleHeight <= 0) return;

    const sectionVisibility = rect.height > 0 ? visibleHeight / rect.height : 0;
    const viewportCoverage = visibleHeight / visibleViewportHeight;
    const qualifies =
      sectionVisibility >= MOBILE_ACTIVE_VISIBILITY_RATIO ||
      viewportCoverage >= MOBILE_ACTIVE_VISIBILITY_RATIO;

    if (!qualifies) return;

    const itemCenter = rect.top + rect.height / 2;
    const centerDistance = Math.abs(itemCenter - viewportCenter);

    if (centerDistance < bestCenterDistance) {
      bestCenterDistance = centerDistance;
      activeIdx = idx;
    }
  });

  return activeIdx;
}

function createMobileActiveIndexSpy(
  content: HTMLDivElement,
  itemRefs: RefObject<Array<HTMLElement | null>>,
  mobileBarHeightRef: RefObject<number>,
  isProgrammaticScrollRef: RefObject<boolean>,
  setActiveIndex: Dispatch<SetStateAction<number>>,
) {
  const syncMobileActiveIndex = () => {
    if (isProgrammaticScrollRef.current) return;

    const nextActiveIndex = getMobileActiveIndexFromViewport(
      itemRefs.current ?? [],
      mobileBarHeightRef.current ?? 0,
    );
    if (nextActiveIndex < 0) return;

    setActiveIndex((current) => (current === nextActiveIndex ? current : nextActiveIndex));
  };

  const mobileActiveSpyTrigger = ScrollTrigger.create({
    trigger: content,
    start: 'top bottom',
    end: 'bottom top',
    invalidateOnRefresh: true,
    onUpdate: syncMobileActiveIndex,
  });

  ScrollTrigger.addEventListener('scrollEnd', syncMobileActiveIndex);
  syncMobileActiveIndex();

  return () => {
    ScrollTrigger.removeEventListener('scrollEnd', syncMobileActiveIndex);
    mobileActiveSpyTrigger.kill();
  };
}

function getDesktopPinScrollDistance(leftColumnPin: HTMLDivElement, content: HTMLDivElement) {
  const leftHeight = Math.max(
    leftColumnPin.offsetHeight,
    leftColumnPin.getBoundingClientRect().height,
  );
  const contentHeight = Math.max(content.scrollHeight, content.offsetHeight);

  return Math.max(contentHeight - leftHeight, 0);
}

function createDesktopLeftColumnPin(leftColumnPin: HTMLDivElement, content: HTMLDivElement) {
  const headerHeight = () => getSiteHeaderHeight();

  return ScrollTrigger.create({
    trigger: leftColumnPin,
    start: () => `top top+=${headerHeight()}`,
    end: () => `+=${getDesktopPinScrollDistance(leftColumnPin, content)}`,
    pin: leftColumnPin,
    pinSpacing: true,
    anticipatePin: 1,
    invalidateOnRefresh: true,
  });
}

function createDesktopActiveIndexSpy(
  content: HTMLDivElement,
  itemRefs: RefObject<Array<HTMLElement | null>>,
  isProgrammaticScrollRef: RefObject<boolean>,
  setActiveIndex: Dispatch<SetStateAction<number>>,
) {
  const syncDesktopActiveIndex = () => {
    if (isProgrammaticScrollRef.current) return;

    const nextActiveIndex = getDesktopActiveIndexFromViewport(itemRefs.current ?? []);
    setActiveIndex((current) => (current === nextActiveIndex ? current : nextActiveIndex));
  };

  const desktopActiveSpyTrigger = ScrollTrigger.create({
    trigger: content,
    start: 'top bottom',
    end: 'bottom top',
    invalidateOnRefresh: true,
    onUpdate: syncDesktopActiveIndex,
  });

  ScrollTrigger.addEventListener('scrollEnd', syncDesktopActiveIndex);
  syncDesktopActiveIndex();

  return () => {
    ScrollTrigger.removeEventListener('scrollEnd', syncDesktopActiveIndex);
    desktopActiveSpyTrigger.kill();
  };
}

function bindDesktopImageLoadRefresh(
  content: HTMLDivElement,
  refreshDesktopScrollTriggers: () => void,
) {
  const images = Array.from(content.querySelectorAll('img'));
  if (images.length === 0) return undefined;

  let pending = images.filter((img) => !img.complete).length;
  if (pending === 0) return undefined;

  const handleImageSettled = () => {
    pending -= 1;
    if (pending <= 0) {
      refreshDesktopScrollTriggers();
    }
  };

  images.forEach((img) => {
    if (img.complete) return;
    img.addEventListener('load', handleImageSettled, { once: true });
    img.addEventListener('error', handleImageSettled, { once: true });
  });

  return undefined;
}

/** Desktop: animate the vertical progress line as each item scrolls past. */
function setupDesktopProgressLine(
  progressLine: HTMLSpanElement | null,
  items: Array<HTMLElement | null>,
) {
  const totalItems = items.filter(Boolean).length;
  if (!progressLine || totalItems === 0) return;

  gsap.set(progressLine, { scaleY: 0, transformOrigin: 'top center' });
  items.forEach((itemEl, idx) => {
    if (!itemEl) return;
    const fromScale = idx / totalItems;
    const toScale = (idx + 1) / totalItems;
    const nextItem = items[idx + 1];
    gsap.fromTo(
      progressLine,
      { scaleY: fromScale },
      {
        scaleY: toScale,
        ease: 'none',
        scrollTrigger: {
          trigger: itemEl,
          start: 'top 72%',
          endTrigger: nextItem ?? itemEl,
          end: nextItem ? 'top 72%' : 'bottom 18%',
          scrub: 0.3,
          invalidateOnRefresh: true,
        },
      },
    );
  });
}

/**
 * Set the reveal animation for a single timeline item. On reduced-motion or
 * non-desktop, everything is simply shown; otherwise a scroll-triggered reveal
 * timeline is built.
 */
function setupItemReveal(
  item: HTMLElement,
  imageFrame: HTMLDivElement | null,
  prefersReducedMotion: boolean,
  isDesktop: boolean,
) {
  const copy = item.querySelector('[data-timeline-copy]');
  const image = imageFrame?.querySelector('img');
  const divider = item.querySelector('[data-timeline-divider]');

  if (prefersReducedMotion || !isDesktop) {
    gsap.set([item, copy, imageFrame, image, divider].filter(Boolean), {
      autoAlpha: 1,
      y: 0,
      yPercent: 0,
      scale: 1,
      filter: 'blur(0px)',
      clipPath: 'inset(0% 0% 0% 0%)',
    });
    return;
  }

  gsap.set(item, { autoAlpha: 0 });
  gsap.set(copy, { autoAlpha: 0, y: 70, filter: 'blur(10px)' });
  gsap.set(imageFrame, {
    autoAlpha: 0,
    y: 48,
  });
  if (image) {
    gsap.set(image, { yPercent: 0, scale: 1 });
  }
  gsap.set(divider, { scaleX: 0, transformOrigin: 'left center' });

  const reveal = gsap.timeline({
    defaults: { ease: 'power3.out' },
    scrollTrigger: {
      trigger: item,
      start: 'top 78%',
      end: 'bottom 42%',
      toggleActions: 'play none none reverse',
    },
  });

  reveal
    .to(item, { autoAlpha: 1, duration: 0.01 }, 0)
    .to(copy, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 0.85 }, 0)
    .to(
      imageFrame,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.95,
      },
      0.12,
    )
    .to(divider, { scaleX: 1, duration: 0.8 }, 0.2);

  // Desktop: keep image static so scroll never exposes frame gaps.
}

export type HistoryTimelineProps = {
  theme?: HistoryTimelineTheme;
  heading?: string;
  subheading?: string;
  className?: string;
  timelineComponent?: AboutTimelineSection;
};

export default function HistoryTimeline({
  theme = 'light',
  heading,
  subheading,
  className,
  timelineComponent,
}: HistoryTimelineProps) {
  const items = useMemo<HistoryItem[]>(() => {
    if (!timelineComponent?.dataWrapper?.length) return [];

    return timelineComponent.dataWrapper.flatMap((yearWrapper, wrapperIdx) => {
      const events = yearWrapper.data ?? [];
      if (events.length === 0) return [];

      return events.map((event, eventIdx) => ({
        id: `cms-${yearWrapper.id ?? wrapperIdx}-${event.id ?? eventIdx}`,
        year: yearWrapper.title,
        title: event.title,
        description: stripHtml(event.description),
        image:
          (event.media?.url ? resolveStrapiMediaUrl(event.media.url) : null) ||
          '/images/aboutImg.png',
      }));
    });
  }, [timelineComponent]);

  const resolvedHeading = heading || timelineComponent?.title || DEFAULT_HEADING;
  const resolvedSubheading =
    subheading || stripHtml(timelineComponent?.subHeading) || DEFAULT_SUBHEADING;

  const isDark = theme === 'dark';
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileBarFixed, setIsMobileBarFixed] = useState(false);
  const [mobileBarHeight, setMobileBarHeight] = useState(0);
  const [headerOffsetPx, setHeaderOffsetPx] = useState(88);
  const [canPortal, setCanPortal] = useState(false);

  const sectionRef = useRef<HTMLElement | null>(null);
  const leftColumnPinRef = useRef<HTMLDivElement | null>(null);
  const timelinePinRef = useRef<HTMLDivElement | null>(null);
  const mobileBarSlotRef = useRef<HTMLDivElement | null>(null);
  const mobileBarRef = useRef<HTMLDivElement | null>(null);
  const mobileBarHeightRef = useRef(0);
  const timelineRef = useRef<HTMLElement | null>(null);
  const mobileTimelineRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const imageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const desktopYearButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const mobileYearButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const progressLineRef = useRef<HTMLSpanElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number | null>(null);

  const refreshDesktopScrollTriggers = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    window.requestAnimationFrame(() => ScrollTrigger.refresh());
  }, []);

  const scrollToItem = useCallback((index: number) => {
    const item = itemRefs.current[index];
    if (!item || typeof window === 'undefined') return;

    const isMobile = window.innerWidth < 1024;
    setActiveIndex(index);

    if (programmaticScrollTimerRef.current !== null) {
      window.clearTimeout(programmaticScrollTimerRef.current);
    }

    isProgrammaticScrollRef.current = true;

    if (isMobile) {
      const headerOffset = getSiteHeaderHeight();
      const barHeight = mobileBarHeightRef.current || 0;
      const top = Math.max(
        0,
        window.scrollY + item.getBoundingClientRect().top - headerOffset - barHeight - 8,
      );

      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      const rect = item.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const targetTop = window.scrollY + rect.top + rect.height / 2 - viewportHeight / 2;

      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    programmaticScrollTimerRef.current = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      programmaticScrollTimerRef.current = null;
    }, 1000);
  }, []);

  const prevActiveIndexRef = useRef(activeIndex);
  const navScrollRafRef = useRef<number | null>(null);
  const mobileNavScrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    setCanPortal(true);
  }, []);

  // Mobile: fixed year bar with in-flow placeholder (no GSAP pin, no CSS sticky)
  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileBarFixed(false);
      return undefined;
    }

    const slot = mobileBarSlotRef.current;
    const bar = mobileBarRef.current;
    const section = sectionRef.current;
    if (!slot || !bar || !section) return undefined;

    let frameId = 0;

    const updateMobileBar = () => {
      frameId = 0;

      const headerOffset = getSiteHeaderHeight();
      const measuredHeight = bar.offsetHeight;
      const barHeight = measuredHeight > 0 ? measuredHeight : mobileBarHeightRef.current;
      const slotRect = slot.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();

      const sectionActive = sectionRect.bottom > headerOffset + 8;
      const shouldFix = sectionActive && slotRect.top <= headerOffset;

      setHeaderOffsetPx(headerOffset);
      mobileBarHeightRef.current = barHeight;
      setMobileBarHeight(barHeight);
      setIsMobileBarFixed(shouldFix);
      section.style.setProperty('--mobile-timeline-height', `${barHeight}px`);
    };

    const scheduleUpdate = () => {
      if (frameId !== 0) return;
      frameId = window.requestAnimationFrame(updateMobileBar);
    };

    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleUpdate) : null;
    resizeObserver?.observe(bar);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [isMobileViewport, items.length]);

  useEffect(() => {
    if (!isMobileViewport) return undefined;

    const frameId = window.requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => window.cancelAnimationFrame(frameId);
  }, [isMobileViewport, mobileBarHeight]);

  // Mobile: center active year card in the horizontal nav
  useEffect(() => {
    if (!isMobileViewport) return undefined;

    if (mobileNavScrollRafRef.current !== null) {
      window.cancelAnimationFrame(mobileNavScrollRafRef.current);
    }

    mobileNavScrollRafRef.current = window.requestAnimationFrame(() => {
      mobileNavScrollRafRef.current = window.requestAnimationFrame(() => {
        mobileNavScrollRafRef.current = null;

        const timeline = mobileTimelineRef.current;
        const activeButton = mobileYearButtonRefs.current[activeIndex];
        if (!timeline || !activeButton) return;

        const buttonRect = activeButton.getBoundingClientRect();
        const screenCenter = window.innerWidth / 2;
        const targetScrollLeft =
          timeline.scrollLeft + (buttonRect.left + buttonRect.width / 2) - screenCenter;
        const maxScrollLeft = timeline.scrollWidth - timeline.clientWidth;

        timeline.scrollTo({
          left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
          behavior: 'smooth',
        });
      });
    });

    return () => {
      if (mobileNavScrollRafRef.current !== null) {
        window.cancelAnimationFrame(mobileNavScrollRafRef.current);
        mobileNavScrollRafRef.current = null;
      }
    };
  }, [activeIndex, isMobileViewport, isMobileBarFixed]);

  // Desktop only: scroll active year into view in vertical nav
  useEffect(() => {
    if (isMobileViewport) return undefined;

    const timeline = timelineRef.current;
    const activeButton = desktopYearButtonRefs.current[activeIndex];
    if (!timeline || !activeButton) return undefined;

    if (navScrollRafRef.current !== null) {
      window.cancelAnimationFrame(navScrollRafRef.current);
    }

    const wasScrollingUp = activeIndex < prevActiveIndexRef.current;
    prevActiveIndexRef.current = activeIndex;

    navScrollRafRef.current = window.requestAnimationFrame(() => {
      navScrollRafRef.current = null;

      const timelineRect = timeline.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const scrollPadding = 28;
      let delta = 0;

      if (buttonRect.top < timelineRect.top + scrollPadding) {
        delta = buttonRect.top - timelineRect.top - scrollPadding;
      } else if (buttonRect.bottom > timelineRect.bottom - scrollPadding) {
        delta = buttonRect.bottom - timelineRect.bottom + scrollPadding;
      } else if (wasScrollingUp) {
        const targetCenter = timelineRect.top + timelineRect.height * 0.34;
        const buttonCenter = buttonRect.top + buttonRect.height / 2;
        if (buttonCenter > targetCenter + scrollPadding) {
          delta = buttonCenter - targetCenter;
        }
      }

      if (Math.abs(delta) > 1) {
        timeline.scrollTo({ top: timeline.scrollTop + delta, behavior: 'auto' });
      }
    });

    return () => {
      if (navScrollRafRef.current !== null) {
        window.cancelAnimationFrame(navScrollRafRef.current);
        navScrollRafRef.current = null;
      }
    };
  }, [activeIndex, isMobileViewport]);

  useEffect(
    () => () => {
      if (programmaticScrollTimerRef.current !== null) {
        window.clearTimeout(programmaticScrollTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const nav = timelineRef.current;
    if (!nav || typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    const forwardWheel = (e: WheelEvent) => {
      e.preventDefault();
      window.scrollBy({ top: e.deltaY, left: 0 });
    };

    nav.addEventListener('wheel', forwardWheel, { passive: false });
    return () => nav.removeEventListener('wheel', forwardWheel);
  }, []);

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isDesktop =
        typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

      const media = gsap.matchMedia();

      media.add('(max-width: 1023px)', () => {
        const content = contentRef.current;
        if (!content) return undefined;

        const cleanupMobileActiveSpy = createMobileActiveIndexSpy(
          content,
          itemRefs,
          mobileBarHeightRef,
          isProgrammaticScrollRef,
          setActiveIndex,
        );

        return () => {
          cleanupMobileActiveSpy();
        };
      });

      media.add('(min-width: 1024px)', () => {
        const leftColumnPin = leftColumnPinRef.current;
        const content = contentRef.current;

        if (!leftColumnPin || !content) {
          return undefined;
        }

        const desktopPinTrigger = createDesktopLeftColumnPin(leftColumnPin, content);
        const cleanupDesktopActiveSpy = createDesktopActiveIndexSpy(
          content,
          itemRefs,
          isProgrammaticScrollRef,
          setActiveIndex,
        );

        setupDesktopProgressLine(progressLineRef.current, itemRefs.current);

        const resizeObserver =
          typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(refreshDesktopScrollTriggers)
            : null;

        resizeObserver?.observe(content);
        resizeObserver?.observe(leftColumnPin);
        itemRefs.current.forEach((itemEl) => {
          if (itemEl) resizeObserver?.observe(itemEl);
        });

        bindDesktopImageLoadRefresh(content, refreshDesktopScrollTriggers);
        refreshDesktopScrollTriggers();

        const delayedRefreshIds = [400, 1200, 2400].map((delay) =>
          window.setTimeout(refreshDesktopScrollTriggers, delay),
        );

        return () => {
          delayedRefreshIds.forEach((id) => window.clearTimeout(id));
          resizeObserver?.disconnect();
          cleanupDesktopActiveSpy();
          desktopPinTrigger.kill();
        };
      });

      itemRefs.current.forEach((item, idx) => {
        if (item) setupItemReveal(item, imageRefs.current[idx], prefersReducedMotion, isDesktop);
      });

      const refreshId = window.requestAnimationFrame(() => ScrollTrigger.refresh());

      return () => {
        window.cancelAnimationFrame(refreshId);
        media.revert();
      };
    },
    { scope: sectionRef, dependencies: [items.length, refreshDesktopScrollTriggers] },
  );

  const sectionBg = isDark ? 'bg-black text-white' : 'bg-[#f0f0f0] text-[#0A0A0A]';
  const eyebrowClass = isDark ? 'text-[#ed1c25]' : 'text-[#c80b0f]';
  const mutedClass = isDark ? 'text-white/60' : 'text-[#0A0A0A]/60';
  const lineClass = isDark ? 'bg-white/10' : 'bg-black/10';
  const activeTimelineClass = isDark ? 'text-white' : 'text-[#0A0A0A]';

  if (items.length === 0) {
    return null;
  }

  const mobileBarBg = isDark ? 'bg-black' : 'bg-[#f0f0f0]';
  const mobileBarShellClass = cn(
    'w-full border-b border-black/10 shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
    mobileBarBg,
  );

  const mobileButtonClassFor = (isActive: boolean): string => {
    if (isActive) {
      return isDark ? 'border-[#ed1c25]/50 bg-white/[0.06]' : 'border-[#ed1c25]/40 bg-white';
    }
    return isDark ? 'border-white/10 bg-white/[0.035]' : 'border-black/10 bg-white/60';
  };

  const mobileDotClassFor = (isActive: boolean, isCompleted: boolean): string => {
    if (isActive || isCompleted) return 'border-[#ed1c25] bg-[#ed1c25]';
    return isDark ? 'border-white/25 bg-black' : 'border-black/20 bg-[#f0f0f0]';
  };

  const renderMobileNavButton = (item: HistoryItem, idx: number, isPortal: boolean) => {
    const isVisibleInstance = isPortal ? isMobileBarFixed : !isMobileBarFixed;
    const isActive = idx === activeIndex;
    const isCompleted = idx < activeIndex;

    return (
      <button
        key={`${item.id}-${isPortal ? 'portal' : 'inline'}-mobile-nav`}
        ref={(el) => {
          if (isVisibleInstance) {
            mobileYearButtonRefs.current[idx] = el;
          }
        }}
        type="button"
        onClick={() => scrollToItem(idx)}
        aria-current={isActive ? 'step' : undefined}
        className={cn(
          'relative grid min-w-[7.25rem] shrink-0 cursor-pointer touch-manipulation select-none grid-cols-[1rem_minmax(0,1fr)] gap-2 rounded-xl border px-2.5 py-2 text-left sm:min-w-[8.5rem]',
          mobileButtonClassFor(isActive),
        )}
      >
        <span
          className={cn(
            'mt-0.5 grid h-3.5 w-3.5 place-items-center rounded-full border sm:h-4 sm:w-4',
            mobileDotClassFor(isActive, isCompleted),
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full bg-white',
              isActive || isCompleted ? 'opacity-100' : 'opacity-0',
            )}
          />
        </span>
        <span className={isActive ? activeTimelineClass : mutedClass}>
          <span
            className={cn(
              'block text-sm font-semibold sm:text-base',
              isActive ? 'text-[#ed1c25]' : '',
            )}
          >
            {item.year}
          </span>
          <span className="mt-1 block line-clamp-1 text-[0.58rem] font-medium sm:text-[0.62rem]">
            {item.title}
          </span>
        </span>
      </button>
    );
  };

  const renderMobileTimelineNav = (options?: { isPortal?: boolean }) => {
    const isPortal = Boolean(options?.isPortal);
    const isVisibleInstance = isPortal ? isMobileBarFixed : !isMobileBarFixed;

    return (
      <nav
        ref={(el) => {
          if (isVisibleInstance) {
            mobileTimelineRef.current = el;
          }
        }}
        className="w-full max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain px-4 py-2 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] sm:px-6 [&::-webkit-scrollbar]:hidden"
        aria-label="Company history timeline"
      >
        <div className="relative min-w-max">
          <div className="flex gap-2 pr-2">
            {items.map((item, idx) => renderMobileNavButton(item, idx, isPortal))}
          </div>
        </div>
      </nav>
    );
  };

  const desktopDotClassFor = (isActive: boolean, isCompleted: boolean): string => {
    if (isActive) {
      return 'border-[#ed1c25] bg-[#ed1c25] shadow-[0_0_0_8px_rgba(237,28,37,0.16),0_0_30px_rgba(237,28,37,0.3)]';
    }
    if (isCompleted) return 'border-[#ed1c25] bg-[#ed1c25]';
    if (isDark) return 'border-white/25 bg-black group-hover:border-white/60';
    return 'border-black/20 bg-[#f0f0f0] group-hover:border-black/50';
  };

  const desktopLabelClassFor = (isActive: boolean): string => {
    if (isActive) return activeTimelineClass;
    if (isDark) return 'text-white/45 group-hover:text-white/75';
    return 'text-[#0A0A0A]/45 group-hover:text-[#0A0A0A]/75';
  };

  const desktopYearClassFor = (isActive: boolean): string => {
    if (isActive) return 'scale-150 text-[#ed1c25]';
    if (isDark) return 'text-white/45';
    return 'text-[#0A0A0A]/45';
  };

  const desktopTitleClassFor = (isActive: boolean): string => {
    if (isActive) return isDark ? 'text-white/75' : 'text-[#0A0A0A]/65';
    return mutedClass;
  };

  const renderDesktopTimelineNav = () => (
    <nav
      ref={(el) => {
        timelineRef.current = el;
      }}
      className="h-full max-h-full w-full max-w-full overflow-x-hidden overflow-y-auto overscroll-contain px-3 pb-6 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Company history timeline"
    >
      <div className="relative min-w-0 pl-1">
        <span
          aria-hidden
          className={cn('absolute bottom-4 left-[0.82rem] top-4 block w-px', lineClass)}
        />
        <span
          ref={progressLineRef}
          aria-hidden
          className="absolute bottom-4 left-[0.82rem] top-4 block w-px origin-top bg-[#ed1c25]"
        />
        <div className="block space-y-0.5">
          {items.map((item, idx) => {
            const isActive = idx === activeIndex;
            const isCompleted = idx < activeIndex;

            const desktopDotClass = desktopDotClassFor(isActive, isCompleted);
            const desktopLabelClass = desktopLabelClassFor(isActive);
            const desktopYearClass = desktopYearClassFor(isActive);
            const desktopTitleClass = desktopTitleClassFor(isActive);

            return (
              <button
                key={`${item.id}-desktop-nav`}
                ref={(el) => {
                  desktopYearButtonRefs.current[idx] = el;
                }}
                type="button"
                onClick={() => scrollToItem(idx)}
                aria-current={isActive ? 'step' : undefined}
                className="group relative grid w-full min-w-0 cursor-pointer touch-manipulation grid-cols-[1.65rem_minmax(0,1fr)] gap-4 border-0 bg-transparent px-0 py-2 text-left transition-all duration-500"
              >
                <span
                  className={cn(
                    'relative z-10 mt-0.5 grid h-5 w-5 place-items-center rounded-full border transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    desktopDotClass,
                  )}
                >
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full transition-transform duration-500',
                      isActive || isCompleted ? 'scale-100 bg-white' : 'scale-0 bg-[#ed1c25]',
                    )}
                  />
                  {isActive ? (
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full border border-[#ed1c25] animate-ping"
                    />
                  ) : null}
                </span>

                <span
                  className={cn(
                    'transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    desktopLabelClass,
                  )}
                >
                  <span
                    className={cn(
                      'inline-block origin-left text-xl font-semibold leading-none tracking-[-0.03em] transition-[transform,color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                      desktopYearClass,
                    )}
                  >
                    {item.year}
                  </span>
                  <span
                    className={cn(
                      'mt-1 block line-clamp-2 text-[0.68rem] font-medium leading-snug transition-colors duration-500',
                      desktopTitleClass,
                    )}
                  >
                    {item.title}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );

  const renderArticles = () =>
    items.map((item, idx) => (
      <article
        key={item.id}
        id={`history-timeline-item-${idx}`}
        ref={(el) => {
          itemRefs.current[idx] = el;
        }}
        className="history-timeline-item w-full min-w-0 scroll-mt-[calc(var(--site-header-height,5.5rem)+var(--mobile-timeline-height,4.25rem))] opacity-100 lg:scroll-mt-28 lg:opacity-0"
      >
        <div data-timeline-copy className="min-w-0">
          <h3 className="text-3xl font-semibold leading-none tracking-[-0.05em] sm:text-4xl lg:mt-5 lg:text-5xl">
            <span className={eyebrowClass}>{item.year}</span>
            <span className="mt-2 block">{item.title}</span>
          </h3>
          <p
            className={cn(
              'mt-5 max-w-2xl break-words text-sm font-medium leading-5 sm:mt-6 sm:text-base sm:leading-6 lg:mt-7 lg:text-lg lg:leading-7',
              mutedClass,
            )}
          >
            {item.description}
          </p>
        </div>

        <div
          ref={(el) => {
            imageRefs.current[idx] = el;
          }}
          className={cn(
            'relative mx-auto mt-6 w-full min-w-0 sm:mt-6 lg:mt-8',
            'max-lg:overflow-hidden max-lg:rounded-[1.15rem] max-lg:bg-black sm:max-lg:rounded-[1.35rem]',
            'lg:will-change-[transform,opacity]',
          )}
        >
          <CmsImage
            src={item.image}
            alt={`${item.title} visual`}
            width={1200}
            height={800}
            priority={idx === 0}
            deferVideoLoad
            sizes="(max-width: 1024px) 92vw, 58vw"
            className="h-auto w-full rounded-[1.15rem] object-contain sm:rounded-[1.35rem] lg:rounded-[1.35rem]"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>

        {idx < items.length - 1 ? (
          <div data-timeline-divider className={cn('mt-14 h-px w-full', lineClass)} />
        ) : null}
      </article>
    ));

  return (
    <section
      ref={sectionRef}
      data-section-blur="false"
      className={cn('relative', sectionBg, className)}
      style={{ ['--mobile-timeline-height' as string]: `${mobileBarHeight || 68}px` }}
    >
      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid min-w-0 gap-10 max-lg:gap-0 md:gap-12 lg:grid-cols-[0.42fr_0.58fr] lg:gap-16 xl:gap-24">
          <aside className="relative min-w-0 max-lg:contents lg:col-start-1 lg:row-start-1 lg:block lg:self-start">
            <div
              ref={leftColumnPinRef}
              className="min-w-0 max-lg:contents lg:flex lg:max-h-[calc(100vh-var(--site-header-height,5.5rem))] lg:flex-col lg:self-start"
            >
              <div className="min-w-0 shrink-0 max-lg:order-1">
                <h2 className="mt-2 max-w-xl text-3xl font-semibold leading-[0.98] tracking-[-0.05em] sm:text-4xl md:text-5xl lg:mt-4 lg:text-[4.75rem]">
                  {resolvedHeading}
                </h2>
                <p
                  className={cn(
                    'mt-4 max-w-2xl text-sm font-medium leading-7 sm:mt-5 sm:text-lg lg:mt-6 lg:max-w-md max-sm:mb-5',
                    mutedClass,
                  )}
                >
                  {resolvedSubheading}
                </p>
              </div>

              <div
                ref={timelinePinRef}
                className="pointer-events-auto relative z-30 mt-7 hidden min-h-0 min-w-0 lg:mt-10 lg:flex lg:flex-1 lg:flex-col"
              >
                {renderDesktopTimelineNav()}
              </div>
            </div>
          </aside>

          <div className="flex min-w-0 flex-col max-lg:order-2 lg:col-start-2 lg:row-start-1">
            <div ref={mobileBarSlotRef} className="relative lg:hidden">
              <div
                aria-hidden
                className="w-full shrink-0"
                style={{ height: isMobileBarFixed ? mobileBarHeight : 0 }}
              />
              <div
                ref={mobileBarRef}
                className={cn(
                  mobileBarShellClass,
                  isMobileBarFixed
                    ? 'pointer-events-none invisible absolute top-0 left-0 w-screen max-w-[100vw] [margin-left:calc(50%-50vw)]'
                    : 'relative w-screen max-w-[100vw] [margin-left:calc(50%-50vw)]',
                )}
                aria-hidden={isMobileBarFixed}
              >
                {renderMobileTimelineNav({ isPortal: false })}
              </div>
            </div>

            <div
              ref={contentRef}
              className="relative z-0 min-w-0 space-y-12 pt-5 sm:space-y-16 sm:pt-3 lg:space-y-24 lg:pt-0"
            >
              {renderArticles()}
            </div>
          </div>
        </div>
      </div>

      {canPortal && isMobileViewport && isMobileBarFixed
        ? createPortal(
            <div
              className={cn(mobileBarShellClass, 'fixed inset-x-0 z-[99]')}
              style={{ top: `${headerOffsetPx}px` }}
            >
              {renderMobileTimelineNav({ isPortal: true })}
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}
